import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import filter from 'ramda/src/filter';
import find from 'ramda/src/find';
import findIndex from 'ramda/src/findIndex';
import propEq from 'ramda/src/propEq';
import isNil from 'ramda/src/isNil';
import clone from 'ramda/src/clone';
import move from 'ramda/src/move';
import remove from 'ramda/src/remove';
import CheckIcon from '@components/@core/CheckIcon';
import Thumbnail from '@components/@core/Thumbnail';
import Pagination from '@components/@core/Pagination';
import ProgressBar from '@components/@core/ProgressBar';
import EditDropdown from '@components/EditDropdown';
import AppActions from '~/actions/app';
import { inPage, length } from '~/services/util';

const CourseHeader = ({ editable }) => (
    <div className="career-required-instances">
        <div className="quotas">
            <div className="list py-2 desktop-screen">
                <div className="d-flex-7">
                    <div className="dsl-m12">Course</div>
                </div>
                <div className="d-flex-1">
                    <div className="dsl-m12">Modules</div>
                </div>
                <div className="d-flex-2">
                    <div className="dsl-m12">Completed</div>
                </div>
                <div className="d-flex-2">
                    <div className="dsl-m12">Status</div>
                </div>
                {editable && <div className="w-5"></div>}
            </div>
        </div>
    </div>
);

const TrainingCourse = ({ index, course, dotsMenu, description, editable, handleSelectMenu, name, thumbnail, modules, completed, status, onClickCourse }) => (
    <div className="library-course-card border-bottom p-0" onClick={() => onClickCourse(0, 'course')}>
        <div className={`lib-content d-flex ssm-column training-course ${index === 0 && 'border-0'}`}>
            <div className="d-flex d-flex-1">
                <div className="d-flex-7 d-flex">
                    <CheckIcon className="me-3" size={26} checked={!isNil(status)} onClick={() => onClickCourse(0, 'course')} />
                    <Thumbnail source={thumbnail} />
                    <div className="px-3">
                        <p className="dsl-b16 text-400 mb-0 word-break">{name}</p>
                        <p className="dsl-b16 mb-0 word-break">{description}</p>
                    </div>
                </div>
                <div className="d-flex-1">
                    <p className="dsl-b16 mb-0">{modules}</p>
                </div>
                <div className="d-flex-2">
                    <p className="dsl-b16 mb-0">{completed}</p>
                </div>
                <div className="d-flex-2">
                    <p className="dsl-b16 mb-0">{!isNil(status) ? 'Completed' : 'Incomplete'}</p>
                </div>
                {editable && (
                    <div className="edit w-5">
                        <EditDropdown options={dotsMenu} onChange={handleSelectMenu(course)} />
                    </div>
                )}
            </div>
        </div>
    </div>
);

const TrainingCourses = ({ data, isEditable = false, onChange }) => {
    const dispatch = useDispatch();

    const completedCourses = filter((e) => !isNil(e?.completed_at), data);
    const progress = data.length > 0 ? ((completedCourses.length / data.length) * 100).toFixed(2) : 100;

    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(5);

    const modalData = useSelector((state) => state.app.modalData);
    const career = useSelector((state) => state.manage.careerReport);
    const programs = career?.programs || [];
    const program = find((e) => e.id === data[0]?.program_id, programs);
    const courses = program?.courses || [];

    const showCourse = (idx, card, type) => {
        if (card.data.child_count === 0 || isNil(card.data.child_count)) {
            toast.warn(`Sorry, the course has no modules.`, {
                position: toast.POSITION.BOTTOM_CENTER,
                pauseOnFocusLoss: false,
                hideProgressBar: true,
            });
            return;
        }
        const remainingCount = card.data.child_count - (card.data?.completed || 0);
        const index = remainingCount === 0 && idx === 0 ? 0 : remainingCount !== 0 && idx === 0 && type == 'course' ? card.data.completed : idx;

        const _modalData = clone(modalData);
        if (isNil(index)) return;
        if (isNil(_modalData.before)) _modalData.before = {};

        dispatch(AppActions.uploadSuccess(''));

        const course = find((e) => e.id === card.id, courses);

        if (isNil(card)) {
            _modalData.before.module = _modalData.before.course.children[index];
            _modalData.before.prevmodule = _modalData.before.course.children[index - 1];
            _modalData.before.index = index;
            _modalData.before.page = page;
            _modalData.before.perPage = perPage;
        } else {
            _modalData.before.course = course;
            _modalData.before.module = card;
            _modalData.before.prevmodule = data[index - 1];
            _modalData.before.index = index;
            _modalData.before.page = page;
            _modalData.before.perPage = perPage;
        }

        dispatch(AppActions.modalRequest({ type: 'Preview', data: _modalData }));
    };

    const handleSelectMenu = (item) => (e) => {
        const index = findIndex(propEq('id', item.id), data);
        let modules = clone(data);
        switch (e) {
            case 'move up':
                modules = move(index, index - 1, modules);
                break;
            case 'move down':
                modules = move(index, index + 1, modules);
                break;
            case 'remove':
                modules = remove(index, 1, modules);
                break;
            default:
                break;
        }
        onChange(modules);
    };

    return (
        <div className="lib-programs-career mt-5">
            <div className="d-flex ai-center jc-between mb-3 pt-5">
                <p className="dsl-b18 text-500 mb-0">Learning</p>
            </div>

            <CourseHeader editable={isEditable} />
            {data.length > 0 ? (
                <>
                    {data.map((course, index) => {
                        // Create a copy with sanitized description instead of mutating the original
                        const sanitizedCourse = {
                            ...course,
                            data: {
                                ...course.data,
                                description: course?.data?.description?.replace(/<[^>]+>/g, '') || ''
                            }
                        };

                        let dotsMenu = ['Move Down', 'Move Up', 'Remove'];
                        if (index === 0) {
                            dotsMenu = filter((x) => x !== 'Move Up', dotsMenu);
                        }
                        if (length(data) === index + 1) {
                            dotsMenu = filter((x) => x !== 'Move Down', dotsMenu);
                        }
                        if (inPage(index, page, perPage)) {
                            return (
                                <TrainingCourse
                                    index={index}
                                    key={`${sanitizedCourse.id}-${index}`}
                                    course={sanitizedCourse}
                                    id={sanitizedCourse.id}
                                    name={sanitizedCourse.data.name || sanitizedCourse.name}
                                    description={sanitizedCourse.data.description}
                                    dotsMenu={dotsMenu}
                                    thumbnail={sanitizedCourse.data.thumb_url}
                                    modules={length(sanitizedCourse.data.modules)}
                                    completed={sanitizedCourse.data.completed}
                                    status={sanitizedCourse.data.completed_at}
                                    handleSelectMenu={handleSelectMenu}
                                    editable={isEditable}
                                    onClickCourse={() => showCourse(index, sanitizedCourse, 'course')}
                                />
                            );
                        }
                    })}
                    {data.length >= perPage && (
                        <Pagination current={page} per={perPage} pers={[5, 'all']} total={Math.ceil(data?.length / perPage)} onChange={(e) => setPage(e)} onPer={(e) => setPerPage(e)} />
                    )}
                </>
            ) : (
                <p className="dsl-b16 py-4 mb-0 border-bottom">No Learning Assigned</p>
            )}
            {!isEditable && (
                <div className="d-flex jc-end mt-4">
                    <span className="dsl-b-16 bold">Learning completed</span>
                    <span className="dsl-b16 mx-3">{data.filter((item) => item.data?.completed)?.length || 0}/{data.length}</span>
                    <ProgressBar value={progress} />
                </div>
            )}
        </div>
    );
};

export default TrainingCourses;
